import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { postService } from '../services/postService';
import Input, { Textarea } from '../components/ui/Input';
import Select from '../components/ui/Select';
import Button from '../components/ui/Button';
import FileUpload from '../components/ui/FileUpload';
import { POST_TYPES } from '../utils/formatters';

export default function PostFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    title: '',
    text: '',
    post_type: '',
    image: null,
    images: [],
    videos: [],
  });
  const [existingImages, setExistingImages] = useState({
    image_url: '',
    image_urls: [],
    video_urls: [],
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEdit) {
      fetchPost();
    }
  }, [id]);

  const fetchPost = async () => {
    try {
      const post = await postService.getById(id);
      setFormData({
        title: post.title || '',
        text: post.text || '',
        post_type: typeof post.post_type === 'number'
          ? ['', 'NEWS', 'PROJECT', 'PARTNER'][post.post_type]
          : post.post_type || '',
        image: null,
        images: [],
        videos: [],
      });
      setExistingImages({
        image_url: post.image_url || '',
        image_urls: post.image_urls || [],
        video_urls: post.video_urls || [],
      });
    } catch (error) {
      console.error('Error fetching post:', error);
    } finally {
      setFetching(false);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Заголовок обязателен';
    if (!formData.text.trim()) newErrors.text = 'Текст обязателен';
    if (!formData.post_type) newErrors.post_type = 'Выберите тип поста';
    if (!isEdit && !formData.image) newErrors.image = 'Загрузите обложку';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const data = new FormData();
      data.append('title', formData.title);
      data.append('text', formData.text);
      data.append('post_type', formData.post_type.toLowerCase());

      if (formData.image) {
        data.append('image', formData.image);
      }
      formData.images.forEach((file) => {
        data.append('images', file);
      });
      formData.videos.forEach((file) => {
        data.append('videos', file);
      });

      if (isEdit) {
        await postService.update(id, data);
      } else {
        await postService.create(data);
      }
      navigate('/admin/posts');
    } catch (error) {
      console.error('Error saving post:', error);
      setErrors({ submit: 'Ошибка сохранения. Попробуйте ещё раз.' });
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  const postTypeOptions = Object.values(POST_TYPES);

  return (
    <div>
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/posts')}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Назад к постам
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">
          {isEdit ? 'Редактирование поста' : 'Создание поста'}
        </h1>

        {errors.submit && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
            {errors.submit}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <Input
            label="Заголовок"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            error={errors.title}
            placeholder="Введите заголовок поста"
          />

          <Textarea
            label="Текст"
            value={formData.text}
            onChange={(e) => setFormData({ ...formData, text: e.target.value })}
            error={errors.text}
            rows={6}
            placeholder="Введите текст поста"
          />

          <Select
            label="Тип поста"
            value={formData.post_type}
            onChange={(e) => setFormData({ ...formData, post_type: e.target.value })}
            options={postTypeOptions}
            error={errors.post_type}
            placeholder="Выберите тип"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Обложка {!isEdit && <span className="text-red-500">*</span>}
            </label>
            {existingImages.image_url && (
              <div className="mb-4">
                <p className="text-sm text-gray-500 mb-2">Текущая обложка:</p>
                <img
                  src={existingImages.image_url}
                  alt="Current cover"
                  className="h-32 w-auto rounded-lg object-cover"
                />
              </div>
            )}
            <FileUpload
              onChange={(file) => setFormData({ ...formData, image: file })}
              accept="image/*"
              error={errors.image}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Дополнительные изображения
            </label>
            {existingImages.image_urls?.length > 0 && (
              <div className="mb-4">
                <p className="text-sm text-gray-500 mb-2">Текущие изображения:</p>
                <div className="flex gap-2 flex-wrap">
                  {existingImages.image_urls.map((url, i) => (
                    <img
                      key={i}
                      src={url}
                      alt={`Image ${i + 1}`}
                      className="h-20 w-20 rounded-lg object-cover"
                    />
                  ))}
                </div>
              </div>
            )}
            <FileUpload
              onChange={(files) => setFormData({ ...formData, images: files })}
              accept="image/*"
              multiple
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Видео
            </label>
            {existingImages.video_urls?.length > 0 && (
              <div className="mb-4">
                <p className="text-sm text-gray-500 mb-2">
                  Текущие видео: {existingImages.video_urls.length}
                </p>
              </div>
            )}
            <FileUpload
              onChange={(files) => setFormData({ ...formData, videos: files })}
              accept="video/*"
              multiple
              preview={false}
            />
          </div>

          <div className="flex justify-end gap-4">
            <Button type="button" variant="secondary" onClick={() => navigate('/admin/posts')}>
              Отмена
            </Button>
            <Button type="submit" loading={loading}>
              {isEdit ? 'Сохранить' : 'Создать'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
